import { Component, input, signal, computed, OnDestroy, inject } from '@angular/core';
import { RevisionService } from '../../projects.service';
import { StatusSidebar } from '@app/shared/components/status-sidebar/status-sidebar';
import { CrossTabSyncService } from '@app/core/services/cross-tab-sync/cross-tab-sync.service';
import { Subject, takeUntil, filter } from 'rxjs';

@Component({
  selector: 'app-revision-status',
  standalone: true,
  imports: [StatusSidebar],
  templateUrl: './revision-status.html',
  styleUrl: './revision-status.scss'
})
export class RevisionStatus implements OnDestroy {
  revisionId = input<string | null>(null);
  refreshTrigger = input<number>(0);
  
  // Internal refresh trigger signal for cross-tab sync
  private internalRefreshTrigger = signal<number>(0);
  private readonly destroy$ = new Subject<void>();
  private readonly crossTabSyncService = inject(CrossTabSyncService);
  
  // Computed signal that combines input and internal refresh triggers
  effectiveRefreshTrigger = computed(() => {
    return this.refreshTrigger() + this.internalRefreshTrigger();
  });
  
  // Configuration for revision status
  revisionConfig = {
    type: 'revision' as const,
    title: 'Manage Revision',
    statusLabel: 'Status',
    assignLabel: 'Assigned To',
    plannedHoursLabel: 'Planned Hrs',
    dueDateLabel: 'Due on',
    refNumberLabel: 'Ref No.',
    showPlannedHours: true,
    showDueDate: true,
    showRefNumber: true,
    showApprovals: true,
    showTimeSummary: true,
    customFields: []
  };

  constructor(public revisionService: RevisionService) {
    this.setupCrossTabSync();
  }

  /**
   * Setup cross-tab synchronization for revision status updates
   */
  private setupCrossTabSync(): void {
    this.crossTabSyncService.getSyncEvents()
      .pipe(
        takeUntil(this.destroy$),
        filter((event): event is any => !!event && !!event.type)
      )
      .subscribe((event) => {
        const currentRevisionId = this.revisionId();
        if (!currentRevisionId) return;

        // Check if this event is for the current revision
        const eventRevisionId = event.revisionId || event.data?.revisionId;
        
        // Listen for both status updates and general revision updates
        if (eventRevisionId === currentRevisionId && 
            (event.type === 'revision_status_updated' || event.type === 'revision_updated')) {
          // Increment refresh trigger to force status-sidebar to refresh
          this.internalRefreshTrigger.update(value => value + 1);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
